import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { apiRequest } from "./api";
import type { Admin } from "./types";

type AuthValue = {
  admin: Admin | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiRequest<Admin>("/api/admin/auth/me")
      .then((value) => alive && setAdmin(value))
      .catch(() => alive && setAdmin(null))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function login(email: string, password: string) {
    const value = await apiRequest<Admin>("/api/admin/auth/login", {
      method: "POST",
      auth: false,
      body: JSON.stringify({ email, password }),
    });
    setAdmin(value);
  }

  async function logout() {
    await apiRequest<void>("/api/admin/auth/logout", { method: "POST" });
    setAdmin(null);
  }

  const value = useMemo(() => ({ admin, loading, login, logout }), [admin, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
